// components/dashboard/user/CreateVoucher.jsx
import { useState } from "react";
import './CreateVoucher.scss';
import HeaderOne from "../../../HeaderOne";
import Breadcrumb from "../../../Breadcrumb";

const CreateVoucher = () => {
  const [formData, setFormData] = useState({
    recipientName: '',
    recipientEmail: '',
    amount: '',
    message: '',
    occasion: 'birthday',
    deliveryDate: ''
  });
  
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [generatedCode, setGeneratedCode] = useState('');

  const occasions = [
    { value: 'birthday', label: 'Birthday' },
    { value: 'anniversary', label: 'Anniversary' },
    { value: 'wedding', label: 'Wedding' },
    { value: 'graduation', label: 'Graduation' },
    { value: 'thankyou', label: 'Thank You' },
    { value: 'congratulations', label: 'Congratulations' },
    { value: 'general', label: 'General' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.recipientName.trim()) {
      newErrors.recipientName = 'Recipient name is required';
    }
    
    if (!formData.recipientEmail.trim()) {
      newErrors.recipientEmail = 'Recipient email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.recipientEmail)) {
      newErrors.recipientEmail = 'Please enter a valid email address';
    }
    
    if (!formData.amount) {
      newErrors.amount = 'Amount is required';
    } else if (parseFloat(formData.amount) < 10) {
      newErrors.amount = 'Minimum amount is ₹10';
    } else if (parseFloat(formData.amount) > 10000) {
      newErrors.amount = 'Maximum amount is ₹10,000';
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'Personal message is required';
    } else if (formData.message.length > 200) {
      newErrors.message = 'Message cannot exceed 200 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const generateVoucherCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    const segments = [];
    for (let i = 0; i < 3; i++) {
      let segment = '';
      for (let j = 0; j < 4; j++) {
        segment += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      segments.push(segment);
    }
    return segments.join('-');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2500));
      
      const voucherCode = generateVoucherCode();
      setGeneratedCode(voucherCode);
      setSuccess(true);
      
      // Reset form
      setFormData({
        recipientName: '',
        recipientEmail: '',
        amount: '',
        message: '',
        occasion: 'birthday',
        deliveryDate: ''
      });
      
    } catch (error) {
      console.error('Error creating voucher:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      recipientName: '',
      recipientEmail: '',
      amount: '',
      message: '',
      occasion: 'birthday',
      deliveryDate: ''
    });
    setErrors({});
    setSuccess(false);
    setGeneratedCode('');
  };

  const getExpiryDate = () => {
    const date = new Date();
    date.setFullYear(date.getFullYear() + 1);
    return date.toLocaleDateString('en-IN', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const getContainerClass = () => {
    let baseClass = 'create-voucher-container';
    if (loading) baseClass += ' loading-state';
    return baseClass;
  };

  return (
    <>
    <HeaderOne/>

    {/* Breadcrumb */}
    <Breadcrumb />
    
    <div className={getContainerClass()}>
      {/* Success Message */}
      {success && (
        <div className="success-message">
          <div className="success-icon">✓</div>
          <div className="success-text">
            <p className="success-title">Gift Voucher Created Successfully!</p>
            <p className="success-details">
              Voucher code: {generatedCode} has been sent to {formData.recipientEmail}
            </p>
          </div>
        </div>
      )}

      {/* Page Header */}
      <div className="page-header">
        <h1>Create Gift Voucher</h1>
        <p className="page-subtitle">
          Create personalized gift vouchers for your friends and family. Share the joy of gifting!
        </p>
      </div>

      {/* Voucher Form */}
      <div className="voucher-form-section">
        <h2 className="section-title">Voucher Details</h2>
        
        <form onSubmit={handleSubmit}>
          <div className="form-grid">
            <div className={`form-field ${errors.recipientName ? 'error' : ''}`}>
              <label htmlFor="recipientName">
                Recipient Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="recipientName"
                name="recipientName"
                value={formData.recipientName}
                onChange={handleInputChange}
                placeholder="Enter recipient's full name"
                disabled={loading}
              />
              {errors.recipientName && (
                <span className="error-message">{errors.recipientName}</span>
              )}
            </div>

            <div className={`form-field ${errors.recipientEmail ? 'error' : ''}`}>
              <label htmlFor="recipientEmail">
                Recipient Email <span className="required">*</span>
              </label>
              <input
                type="email"
                id="recipientEmail"
                name="recipientEmail"
                value={formData.recipientEmail}
                onChange={handleInputChange}
                placeholder="Enter recipient's email"
                disabled={loading}
              />
              {errors.recipientEmail && (
                <span className="error-message">{errors.recipientEmail}</span>
              )}
            </div>

            <div className={`form-field amount-field ${errors.amount ? 'error' : ''}`}>
              <label htmlFor="amount">
                Amount <span className="required">*</span>
              </label>
              <div className="input-wrapper">
                <span className="currency-symbol">₹</span>
                <input
                  type="number"
                  id="amount"
                  name="amount"
                  value={formData.amount}
                  onChange={handleInputChange}
                  placeholder="0"
                  className="amount-input"
                  min="10"
                  max="10000"
                  disabled={loading}
                />
              </div>
              <span className="field-hint">Minimum: ₹10, Maximum: ₹10,000</span>
              {errors.amount && (
                <span className="error-message">{errors.amount}</span>
              )}
            </div>

            <div className="form-field">
              <label htmlFor="occasion">Occasion</label>
              <select
                id="occasion"
                name="occasion"
                value={formData.occasion}
                onChange={handleInputChange}
                disabled={loading}
              >
                {occasions.map(occasion => (
                  <option key={occasion.value} value={occasion.value}>
                    {occasion.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-field">
              <label htmlFor="deliveryDate">Delivery Date (Optional)</label>
              <input
                type="date"
                id="deliveryDate"
                name="deliveryDate"
                value={formData.deliveryDate}
                onChange={handleInputChange}
                min={new Date().toISOString().split('T')[0]}
                disabled={loading}
              />
              <span className="field-hint">Leave blank to send immediately</span>
            </div>

            <div className={`form-field full-width ${errors.message ? 'error' : ''}`}>
              <label htmlFor="message">
                Personal Message <span className="required">*</span>
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                placeholder="Write a heartfelt message for the recipient..."
                rows="4"
                maxLength="200"
                disabled={loading}
              />
              <span className="field-hint">
                {formData.message.length}/200 characters
              </span>
              {errors.message && (
                <span className="error-message">{errors.message}</span>
              )}
            </div>
          </div>
        </form>
      </div>

      {/* Voucher Preview */}
      {(formData.recipientName || formData.amount || formData.message) && (
        <div className="voucher-preview">
          <h2 className="section-title">Voucher Preview</h2>
          
          <div className="voucher-card">
            <div className="voucher-header">
              <h3 className="voucher-title">Gift Voucher</h3>
              <div className="voucher-amount">
                ₹{formData.amount || '0'}
              </div>
            </div>
            
            <div className="voucher-details">
              <div className="recipient-name">
                For: {formData.recipientName || 'Recipient Name'}
              </div>
              <div className="voucher-message">
                "{formData.message || 'Your personal message will appear here...'}"
              </div>
              <div className="voucher-meta">
                <div className="voucher-code">CODE: XXXX-XXXX-XXXX</div>
                <div className="expiry-date">Expires: {getExpiryDate()}</div>
              </div>
            </div>
          </div>
          
          <p className="preview-note">
            This is how your gift voucher will look to the recipient
          </p>
        </div>
      )}

      {/* Action Buttons */}
      <div className="action-buttons">
        <button 
          type="button" 
          className="btn btn-primary"
          onClick={handleReset}
          disabled={loading}
        >
          Reset Form
        </button>
        <button 
          type="submit" 
          className="btn btn-primary"
          onClick={handleSubmit}
          disabled={loading || !formData.recipientName || !formData.recipientEmail || !formData.amount || !formData.message}
        >
          {loading ? 'Creating Voucher...' : 'Create Gift Voucher'}
        </button>
      </div>
    </div>
    </>
  );
};

export default CreateVoucher;